import {fetchNotificationData} from './projects_scheduling_fetch';
import {dispatch, EVENT_ID} from '../../../containers/event_manager';
import {visitGroups} from '../canvas-timeline/canvas_timeline_visitor';
import {GROUP_TYPE, ITEM_TYPE, getCanvasTimelineDateFromMoment} from './../canvas-timeline/canvas_timeline_util';
import TaskItem from './items/task_item';
import TaskGroup from './groups/task_group';
import PhaseItem from './items/phase_item';
import ProjectAllocationItem from './items/project_allocation_item';
import PhaseGroup from './groups/phase_group';
import Util from '../../../forecast-app/shared/util/util';
import {PROJECT_STATUS} from '../../../constants';
import {getRolesMap, getPersonsMap, getPhasesMap, getTasksMap, getProjectGroupMaps, composeTaskItem, composeTaskGroup, composePhaseGroup, composePhaseItem, composeProjectAllocation, composeProject} from './projects_scheduling_data';
import {sortTasksByDate} from '../../insights/components/project_timeline_component';

const eventTypesToHandle = ['TASK', 'SUB_TASK', 'TIME_REG', 'PHASE', 'PROJECT_PERSON', 'PERSON', 'PROJECT', 'IDLE_TIME', 'ALLOCATION'];

const createPhaseGroup = (pageComponent, addedPhaseId, milestonesGroup) => {
	const {phaseIdToPhaseMap} = getPhasesMap(pageComponent.state.data);
	const {phaseIdToTaskIdsMap, taskIdToTaskMap} = getTasksMap(pageComponent.state.data);
	const phase = phaseIdToPhaseMap.get(addedPhaseId);
	const {phaseTimeRegistered, phaseTimeRegisteredNotDone, phaseRemaining, phaseForecast, phaseDoneForecast, phaseTotalTasks, phaseDoneTasks, phaseGroupData} = composePhaseGroup(
		pageComponent,
		phase,
		addedPhaseId,
		phaseIdToTaskIdsMap,
		taskIdToTaskMap
	);
	if (phaseGroupData) {
		const phaseGroup = new PhaseGroup(phaseGroupData);
		if (phaseGroup) {
			milestonesGroup.groups.push(phaseGroup);
			const phaseItemData = composePhaseItem(pageComponent, phase, phaseTimeRegistered, phaseTimeRegisteredNotDone, phaseRemaining, phaseForecast, phaseDoneForecast, phaseTotalTasks, phaseDoneTasks);
			if (phaseItemData) {
				const phaseItem = new PhaseItem(phaseItemData);
				pageComponent.state.items.push(phaseItem);
			}
		}
	}
	milestonesGroup.data.phases.push(phase);
	Util.sortMilestones(milestonesGroup.groups, true);
};

const processData = (data, updatedData) => {
	const projectByProjectId = {};
	const addedProjects = [];
	const taskById = {};
	const addedTasks = [];
	const deletedTaskIds = [];
	const phaseById = {};
	const addedPhases = [];
	const deletedPhaseIds = [];
	const personById = {};
	const addedPersons = [];
	const deletedPersons = [];
	const allocationById = {};
	const addedAllocations = [];
	const deletedAllocationIds = [];
	const dependencyById = {};
	const deletedDependencyIds = [];

	if (updatedData.projects.length > 0) {
		updatedData.projects.forEach(updatedProject => {
			const project = data.projects.find(project => project.id === updatedProject.id);
			if (project) {
				Object.assign(project, updatedProject);
			} else {
				addedProjects.push(updatedProject);
				data.projects.push(updatedProject);
			}

			projectByProjectId[updatedProject.id] = updatedProject;
		});
	}

	if (updatedData.dependencies && updatedData.dependencies.length > 0) {
		updatedData.dependencies.forEach(updatedDependency => {
			const dependency = data.dependencies.find(dependency => dependency.id === updatedDependency.id);
			if (!dependency) {
				data.dependencies.push(updatedDependency);
			}

			dependencyById[updatedDependency.id] = updatedDependency;
		});

		// Find deleted dependencies
		deletedDependencyIds.push(...data.dependencies.filter(existingDependency => !dependencyById[existingDependency.id]).map(existingDependency => existingDependency.id));
	}

	if (updatedData.tasks && updatedData.tasks.length > 0) {
		// Go through tasks to find added/updated tasks
		updatedData.tasks.forEach(updatedTask => {
			const task = data.tasks.find(task => task.id === updatedTask.id);
			if (task) {
				Object.assign(task, updatedTask);
			} else {
				addedTasks.push(updatedTask);
				data.tasks.push(updatedTask);
			}

			taskById[updatedTask.id] = updatedTask;
		});

		// Find deleted tasks
		deletedTaskIds.push(
			...data.tasks
				.filter(existingTask => projectByProjectId[existingTask.projectId])
				.filter(existingTask => !taskById[existingTask.id])
				.map(existingTask => existingTask.id)
		);
	}

	if (updatedData.phases && updatedData.phases.length > 0) {
		// Go through phases to find added/updated phases
		updatedData.phases.forEach(updatedPhase => {
			const phase = data.phases.find(phase => phase.id === updatedPhase.id);
			if (phase) {
				Object.assign(phase, updatedPhase);
			} else {
				addedPhases.push(updatedPhase);
				data.phases.push(updatedPhase);
			}

			phaseById[updatedPhase.id] = updatedPhase;
		});

		// Find deleted phases
		deletedPhaseIds.push(
			...data.phases
				.filter(existingPhase => projectByProjectId[existingPhase.projectId])
				.filter(existingPhase => !phaseById[existingPhase.id])
				.map(existingPhase => existingPhase.id)
		);
	}

	if (updatedData.projectPersons && updatedData.projectPersons.length > 0) {
		// Go through persons to find added/updated persons
		updatedData.projectPersons.forEach(updatedPerson => {
			const person = data.projectPersons.find(person => person.id === updatedPerson.id);
			if (person) {
				Object.assign(person, updatedPerson);
			} else {
				addedPersons.push(updatedPerson);
				data.projectPersons.push(updatedPerson);
			}

			personById[updatedPerson.id] = updatedPerson;
		});

		// Find deleted persons
		deletedPersons.push(
			...data.projectPersons.filter(existingPerson => projectByProjectId[existingPerson.projectId]).filter(existingPerson => !personById[existingPerson.id] && data.persons.find(person => person.id === existingPerson.personId).active)
		);
	}

	if (updatedData.allocations && updatedData.allocations.length > 0) {
		// Go through allocations to find added/updated allocations
		updatedData.allocations.forEach(updatedAllocation => {
			const allocation = data.allocations.find(allocation => allocation.id === updatedAllocation.id);
			if (allocation) {
				Object.assign(allocation, updatedAllocation);
			} else {
				addedAllocations.push(updatedAllocation);
				data.allocations.push(updatedAllocation);
			}

			allocationById[updatedAllocation.id] = updatedAllocation;
		});

		// Find deleted allocations
		deletedAllocationIds.push(...data.allocations.filter(existingAllocation => !allocationById[existingAllocation.id]).map(existingAllocation => existingAllocation.id));
	}

	return {
		projectByProjectId,
		addedProjects,
		taskById,
		addedTasks,
		deletedTaskIds,
		phaseById,
		addedPhases,
		deletedPhaseIds,
		personById,
		addedPersons,
		deletedPersons,
		allocationById,
		addedAllocations,
		deletedAllocationIds,
		dependencyById,
		deletedDependencyIds,
	};
};

const processGroups = (pageComponent, data, processedData) => {
	const {projectByProjectId, phaseById, taskById} = processedData;

	visitGroups(pageComponent.state.groups, group => {
		if (group.groupType === GROUP_TYPE.PROJECT_SCHEDULING_PROJECT) {
			const projectId = group.id;
			const updatedProject = projectByProjectId[projectId];
			if (updatedProject) {
				updatedProject.client = data.clients.find(client => client.id === updatedProject.clientId);
				group.refreshData(updatedProject);
			}
		} else if (group.groupType === GROUP_TYPE.PROJECT_SCHEDULING_PROJECT_SUB_GROUP) {
			const match = group.id.match(/^([a-zA-Z0-9=]+)(phases|team-members)$/);
			const projectId = match[1];
			const updatedProject = projectByProjectId[projectId];
			if (updatedProject) {
				group.refreshData(updatedProject);
			}
		} else if (group.groupType === GROUP_TYPE.PROJECT_SCHEDULING_PHASE) {
			if (!group.id.endsWith('no_milestone')) {
				const updatedPhase = phaseById[group.id];
				if (updatedPhase) {
					group.refreshData(updatedPhase);
				}
			}
		} else if (group.groupType === GROUP_TYPE.PROJECT_SCHEDULING_TASK) {
			const updatedTask = taskById[group.id];
			if (updatedTask) {
				group.refreshData(updatedTask);
			}
		}
	});
};

const processAddedTasks = (pageComponent, processedData) => {
	const {addedTasks, phaseById, projectByProjectId} = processedData;
	addedTasks.forEach(addedTask => {
		let project = pageComponent.state.groups.find(group => group.id === addedTask.projectId);
		const projectById = projectByProjectId[addedTask.projectId];
		if (projectById.isInProjectGroup) {
			const parentGroup = pageComponent.state.groups.find(group => group.id === projectByProjectId[addedTask.projectId].projectGroupId);
			if (parentGroup) {
				project = parentGroup.groups.find(subGroup => subGroup.id === addedTask.projectId);
			}
		}
		if (project) {
			const milestonesGroup = project.groups.find(group => group.groupType === GROUP_TYPE.PROJECT_SCHEDULING_PROJECT_SUB_GROUP && group.id.endsWith('phases'));
			if (milestonesGroup) {
				let phaseGroup = milestonesGroup.groups.find(group => group.id === addedTask.phaseId || (!addedTask.phaseId && group.id.endsWith('no_milestone')));
				if (!phaseGroup) {
					const addedPhase = phaseById[addedTask.phaseId];
					if (addedPhase) {
						createPhaseGroup(pageComponent, addedPhase.id, milestonesGroup);
					}
				} else {
					const taskGroupData = composeTaskGroup(pageComponent, project, addedTask, phaseGroup.id);
					if (taskGroupData) {
						const taskGroup = new TaskGroup(taskGroupData);
						phaseGroup.groups.push(taskGroup);
					}
				}
				const taskItemData = composeTaskItem(pageComponent, addedTask, false);
				if (taskItemData) {
					const taskItem = new TaskItem(taskItemData);
					pageComponent.state.items.push(taskItem);
					pageComponent.state.items = pageComponent.state.items.sort(sortTasksByDate);
				}
			}
		}
	});
};

const processAddedAllocations = (pageComponent, processedData) => {
	const {addedAllocations} = processedData;
	addedAllocations.forEach(addedAllocation => {
		const allocationItemData = composeProjectAllocation(pageComponent, addedAllocation);
		if (allocationItemData !== null) {
			const allocationItem = new ProjectAllocationItem(allocationItemData);
			pageComponent.state.items.push(allocationItem);
		}
	});
};

const processAddedPhases = (pageComponent, processedData) => {
	const {addedPhases} = processedData;
	addedPhases.forEach(addedPhase => {
		const project = pageComponent.state.groups.find(group => group.id === addedPhase.projectId);
		if (project) {
			const milestonesGroup = project.groups.find(group => group.groupType === GROUP_TYPE.PROJECT_SCHEDULING_PROJECT_SUB_GROUP && group.id.endsWith('phases'));
			if (milestonesGroup) {
				createPhaseGroup(pageComponent, addedPhase.id, milestonesGroup);
			}
		}
	});
};

const processAddedProjects = (pageComponent, processedData, data) => {
	const {addedProjects} = processedData;
	const projectGroupMaps = getProjectGroupMaps(pageComponent, data, pageComponent.props);
	addedProjects.forEach(addedProject => {
		const projectGroupHourEstMap = new Map(),
			projectGroupTeamPicturesMap = new Map();
		composeProject(pageComponent, data, pageComponent.props, addedProject, pageComponent.state.items, pageComponent.state.groups, projectGroupMaps, projectGroupHourEstMap, projectGroupTeamPicturesMap);
	});
};

const processAddedPersons = (pageComponent, processedData) => {
	const {addedPersons, projectByProjectId} = processedData;
	addedPersons.forEach(addedPerson => {
		const projectId = addedPerson.projectId;
		let project = pageComponent.state.groups.find(group => group.data.id === projectId);
		const projectById = projectByProjectId[projectId];
		if (projectById && projectById.isInProjectGroup) {
			const parentGroup = pageComponent.state.groups.find(group => group.id === projectByProjectId[projectId].projectGroupId);
			project = parentGroup;
		}
		if (project) {
			const teamGroup = project.groups.find(group => group.groupType === GROUP_TYPE.PROJECT_SCHEDULING_PROJECT_SUB_GROUP && group.id.endsWith('team-members'));
			if (teamGroup) {
				let color = project.projectColor;
				const {personIdToPersonMap} = getPersonsMap(pageComponent.state.data);
				const person = personIdToPersonMap.get(addedPerson.personId);
				const {roleIdToRoleMap} = getRolesMap(pageComponent.state.data);
				const roleName = roleIdToRoleMap.has(addedPerson.roleId) ? roleIdToRoleMap.get(addedPerson.roleId).name : '';
				let projectGroupContainsOnlyHaltedOrDoneProjects;
				if (project.isInProjectGroup) {
					const projectGroupId = project.projectGroupId;
					color = project.data.color;
					projectGroupContainsOnlyHaltedOrDoneProjects = pageComponent.stat.data.projects.filter(project => project.projectGroupId === projectGroupId).every(project => ['HALTED', 'DONE'].includes(project.status));
				}
				if (!teamGroup.groups.find(group => group.data.personId === person.id)) {
					teamGroup.groups.push(pageComponent.createPersonGroup(person, project, addedPerson.roleId, roleName, (project.projectGroupId || project.id) + '-' + person.id, color, projectGroupContainsOnlyHaltedOrDoneProjects));
				}
			}
		}
	});
};

const processUpdatedProject = (pageComponent, item, projectByProjectId) => {
	const updatedProject = projectByProjectId[item.groupId];
	if (updatedProject) {
		item.data.color = updatedProject.projectColor;
		item.data.isPlanning = updatedProject.status === PROJECT_STATUS.PLANNING || updatedProject.status === PROJECT_STATUS.OPPORTUNITY;
		item.data.project.name = updatedProject.name;

		const projectStartDate = Util.CreateNonUtcMomentDate(updatedProject.projectStartYear, updatedProject.projectStartMonth, updatedProject.projectStartDay);
		const projectEndDate = Util.CreateNonUtcMomentDate(updatedProject.projectEndYear, updatedProject.projectEndMonth, updatedProject.projectEndDay);
		item.startDate = projectStartDate ? getCanvasTimelineDateFromMoment(projectStartDate) : null;
		item.endDate = projectEndDate ? getCanvasTimelineDateFromMoment(projectEndDate) : null;
	}
};

const processUpdatedPhase = (item, projectByProjectId, phaseById) => {
	const updatedPhase = phaseById[item.groupId];
	if (updatedPhase) {
		const updatedProject = projectByProjectId[item.data.projectId];
		if (updatedProject) {
			item.data.color = updatedProject.projectColor;
		}
		const phaseStartDate = Util.CreateNonUtcMomentDate(updatedPhase.startYear, updatedPhase.startMonth, updatedPhase.startDay);
		const phaseEndDate = Util.CreateNonUtcMomentDate(updatedPhase.deadlineYear, updatedPhase.deadlineMonth, updatedPhase.deadlineDay);
		item.startDate = phaseStartDate ? getCanvasTimelineDateFromMoment(phaseStartDate) : null;
		item.endDate = phaseEndDate ? getCanvasTimelineDateFromMoment(phaseEndDate) : null;
	}
};

const processUpdatedTask = (pageComponent, item, projectByProjectId, taskById) => {
	const updatedTask = taskById[item.groupId];
	if (updatedTask) {
		const updatedProjectId = item.data.projectId;
		let updatedProject = pageComponent.state.groups.find(group => group.data.id === updatedProjectId);
		const projectById = projectByProjectId[updatedProjectId];
		if (projectById) {
			if (projectById.isInProjectGroup) {
				const parentGroup = pageComponent.state.groups.find(group => group.id === projectByProjectId[updatedProjectId].projectGroupId);
				if (parentGroup) {
					updatedProject = parentGroup.groups.find(subGroup => subGroup.id === updatedProjectId);
				}
			}
			if (updatedProject) {
				item.data.color = updatedProject.projectColor || updatedProject.data.color;
			}
			const taskStartDate = Util.CreateNonUtcMomentDate(updatedTask.startYear, updatedTask.startMonth, updatedTask.startDay);
			const taskEndDate = Util.CreateNonUtcMomentDate(updatedTask.deadlineYear, updatedTask.deadlineMonth, updatedTask.deadlineDay);
			item.startDate = taskStartDate ? getCanvasTimelineDateFromMoment(taskStartDate) : null;
			item.endDate = taskEndDate ? getCanvasTimelineDateFromMoment(taskEndDate) : null;
			item.refreshData(updatedTask);
		}
	}
};

const processUpdatedAllocation = (item, allocationById, pageComponent) => {
	const updatedAllocation = allocationById[item.data.allocation.id];
	if (updatedAllocation) {
		const updatedProjectId = item.data.projectId || item.data.allocation.projectGroupId;
		let updatedProject = pageComponent.state.groups.find(group => group.data.id === updatedProjectId);
		if (updatedProject) {
			if (updatedProject) {
				item.data.color = updatedProject.projectColor || updatedProject.data.color;
			}
			const allocationStartDate = Util.CreateNonUtcMomentDate(updatedAllocation.startYear, updatedAllocation.startMonth, updatedAllocation.startDay);
			const allocationEndDate = Util.CreateNonUtcMomentDate(updatedAllocation.endYear, updatedAllocation.endMonth, updatedAllocation.endDay);
			item.startDate = allocationStartDate ? getCanvasTimelineDateFromMoment(allocationStartDate) : null;
			item.endDate = allocationEndDate ? getCanvasTimelineDateFromMoment(allocationEndDate) : null;
		}
	}
};

const processUpdatedItems = (pageComponent, processedData) => {
	const {projectByProjectId, taskById, phaseById, allocationById} = processedData;
	pageComponent.state.items.forEach(item => {
		switch (item.itemType) {
			case ITEM_TYPE.PROJECT_SCHEDULING_PROJECT:
				processUpdatedProject(pageComponent, item, projectByProjectId, pageComponent);
				break;
			case ITEM_TYPE.PROJECT_SCHEDULING_TASK:
				processUpdatedTask(pageComponent, item, projectByProjectId, taskById);
				break;
			case ITEM_TYPE.PROJECT_SCHEDULING_PHASE:
				processUpdatedPhase(item, projectByProjectId, phaseById);
				break;
			case ITEM_TYPE.PROJECT_SCHEDULING_PROJECT_ALLOCATION:
				processUpdatedAllocation(item, allocationById, pageComponent);
				break;
			default:
			// Do nothing
		}
	});
};

const processDeletedTasks = (pageComponent, processedData) => {
	const {deletedTaskIds} = processedData;
	deletedTaskIds.forEach(deletedTaskId => {
		const itemToDelete = pageComponent.state.items.find(item => item.itemType === ITEM_TYPE.PROJECT_SCHEDULING_TASK && item.data.task.id === deletedTaskId);
		if (itemToDelete) {
			const itemToDeleteIndex = pageComponent.state.items.indexOf(itemToDelete);
			pageComponent.state.items.splice(itemToDeleteIndex, 1);
		}
	});
};

const processDeletedPhases = (pageComponent, processedData) => {
	const {deletedPhaseIds, projectByProjectId} = processedData;
	const {phaseIdToPhaseMap} = getPhasesMap(pageComponent.state.data);
	deletedPhaseIds.forEach(deletedPhaseId => {
		const deletedPhase = phaseIdToPhaseMap.get(deletedPhaseId);
		const updatedProjectId = deletedPhase.projectId;
		let updatedProject = pageComponent.state.groups.find(group => group.data.id === updatedProjectId);
		const projectById = projectByProjectId[updatedProjectId];
		if (projectById.isInProjectGroup) {
			const parentGroup = pageComponent.state.groups.find(group => group.id === projectByProjectId[updatedProjectId].projectGroupId);
			if (parentGroup) {
				updatedProject = parentGroup.groups.find(subGroup => subGroup.id === updatedProjectId);
			}
		}
		if (updatedProject) {
			const milestoneGroup = updatedProject.groups.find(group => group.data.id.endsWith('phases') && group.groupType === GROUP_TYPE.PROJECT_SCHEDULING_PROJECT_SUB_GROUP);
			const groupToDelete = milestoneGroup.groups.find(group => group.data.id === deletedPhaseId);
			if (groupToDelete) {
				const groupToDeleteIndex = milestoneGroup.groups.indexOf(groupToDelete);
				milestoneGroup.groups.splice(groupToDeleteIndex, 1);
			}
		}
	});
};

const processDeletedPersons = (pageComponent, processedData) => {
	const {deletedPersons, projectByProjectId} = processedData;
	deletedPersons.forEach(deletedPerson => {
		const updatedProjectId = deletedPerson.projectId;
		let updatedProject = pageComponent.state.groups.find(group => group.data.id === updatedProjectId);
		const projectById = projectByProjectId[updatedProjectId];
		if (projectById.isInProjectGroup) {
			const parentGroup = pageComponent.state.groups.find(group => group.id === projectById.projectGroupId);
			updatedProject = parentGroup;
		}
		const teamMembersGroup = updatedProject.groups.find(group => group.data.id.endsWith('team-members') && group.groupType === GROUP_TYPE.PROJECT_SCHEDULING_PROJECT_SUB_GROUP);
		const groupToDelete = teamMembersGroup.groups.find(group => group.data.personId === deletedPerson.personId);
		if (groupToDelete) {
			const groupToDeleteIndex = teamMembersGroup.groups.indexOf(groupToDelete);
			teamMembersGroup.groups.splice(groupToDeleteIndex, 1);
		}
	});
};

const processDeletedAllocations = (pageComponent, processedData) => {
	const {deletedAllocationIds} = processedData;
	deletedAllocationIds.forEach(deletedAllocationId => {
		const itemToDelete = pageComponent.state.items.find(item => item.itemType === ITEM_TYPE.PROJECT_SCHEDULING_PROJECT_ALLOCATION && item.data.allocation.id === deletedAllocationId);
		if (itemToDelete) {
			const itemToDeleteIndex = pageComponent.state.items.indexOf(itemToDelete);
			pageComponent.state.items.splice(itemToDeleteIndex, 1);
		}
	});
};

const processDeletedDependencies = (pageComponent, processedData) => {
	const {deletedDependencyIds} = processedData;
	deletedDependencyIds.forEach(deletedDependencyId => {
		const dependencyToDelete = pageComponent.state.dependencies.find(dependency => dependency.id === deletedDependencyId);
		if (dependencyToDelete) {
			const dependencyToDeleteIndex = pageComponent.state.dependencies.indexOf(dependencyToDelete);
			pageComponent.state.dependencies.splice(dependencyToDeleteIndex, 1);
		}
	});
};

// eslint-disable-next-line
const fetchAndProcessData = (pageComponent, projectIdsToUpdate, allocationIdsToUpdate) => {
	fetchNotificationData(projectIdsToUpdate, allocationIdsToUpdate).then(updatedData => {
		updatedData.allocations = updatedData.allocations.filter(allocation =>
			pageComponent.state.data.projects.find(project => project.id === allocation.projectId || (allocation.projectGroupId && project.projectGroupId === allocation.projectGroupId))
		);
		const data = pageComponent.getData();
		if (data) {
			const processedData = processData(data, updatedData);
			processGroups(pageComponent, data, processedData);
			processAddedTasks(pageComponent, processedData);
			processAddedPhases(pageComponent, processedData);
			if (!pageComponent.props.isProjectTimeline) {
				processAddedProjects(pageComponent, processedData, data);
			}
			processAddedAllocations(pageComponent, processedData);
			processAddedPersons(pageComponent, processedData);
			processUpdatedItems(pageComponent, processedData);
			processDeletedTasks(pageComponent, processedData);
			processDeletedPhases(pageComponent, processedData);
			processDeletedAllocations(pageComponent, processedData);
			processDeletedPersons(pageComponent, processedData);
			processDeletedDependencies(pageComponent, processedData);
		}

		dispatch(EVENT_ID.CANVAS_TIMELINE_FORCE_REDRAW, {preventFiltering: false});
	});
};

export const handleNotification = (pageComponent, eventList) => {
	const projectsToUpdate = {};
	const allocationsToUpdate = {};

	eventList.forEach(event => {
		if (eventTypesToHandle.indexOf(event.eventType) > -1) {
			if (event.projectIds) {
				event.projectIds.forEach(projectId => (projectsToUpdate[projectId] = true));
			} else if (event.allocationIds) {
				event.allocationIds.forEach(allocationId => (allocationsToUpdate[allocationId] = true));
			}
		}
	});

	const projectIdsToUpdate = Object.keys(projectsToUpdate);
	const allocationIdsToUpdate = Object.keys(allocationsToUpdate);

	if (projectIdsToUpdate.length === 0 && allocationIdsToUpdate.length === 0) {
		return;
	}

	// Wait a tiny bit before fetching to ensure everything is updated on the backend
	// setTimeout(() => fetchAndProcessData(pageComponent, projectIdsToUpdate, allocationIdsToUpdate), 500);
};
